<template>
  <div class="address-box" :class="{ 'disable-hover': disableHover }">
    <div class="address-box-left">配送至：</div>
    <div class="address-box-right">
      <span class="province">{{ AddressInfo?.provinceName }}</span>
      <span class="city">{{ AddressInfo?.cityName }}</span>
      <span class="district">{{ AddressInfo?.districtName }}</span>
      <span class="subdistrict">{{ AddressInfo?.subdistrictName }}</span>
      <div class="icon-address-arrow"></div>
    </div>
    <!-- 地址选择弹窗，鼠标离开address-box时隐藏 -->
    <div class="address-box-areabox">
      <div class="address-box-areabox-title">
        <!-- 省份标签 -->
        <div
          class="address-box-areabox-title-item"
          :class="{ tabActive: currentAddressType === 'province' }"
          @click="clickTitle('province')"
        >
          {{ chooseAddress?.provinceName || '请选择' }}
        </div>
        <!-- 城市标签（有值或当前为该层级时显示） -->
        <div
          v-if="chooseAddress?.cityName || currentAddressType === 'city' || chooseAddress?.provinceName"
          class="address-box-areabox-title-item"
          :class="{ tabActive: currentAddressType === 'city' }"
          @click="clickTitle('city')"
        >
          {{ chooseAddress?.cityName || '请选择' }}
        </div>
        <!-- 区县标签 -->
        <div
          v-if="chooseAddress?.districtName || currentAddressType === 'district' || chooseAddress?.cityName"
          class="address-box-areabox-title-item"
          :class="{ tabActive: currentAddressType === 'district' }"
          @click="clickTitle('district')"
        >
          {{ chooseAddress?.districtName || '请选择' }}
        </div>
        <!-- 街道标签 -->
        <div
          v-if="chooseAddress?.subdistrictName || currentAddressType === 'subdistrict' || chooseAddress?.districtName"
          class="address-box-areabox-title-item"
          :class="{ tabActive: currentAddressType === 'subdistrict' }"
          @click="clickTitle('subdistrict')"
        >
          {{ chooseAddress?.subdistrictName || '请选择' }}
        </div>
      </div>

      <!-- 列表区域 -->
      <div class="address-box-areabox-content">
        <div
          v-for="item in showList"
          :key="item.code"
          class="address-box-areabox-item"
          :class="{ itemActive: isItemSelected(item.code) }"
          @click="clickArea(currentAddressType, item)"
        >
          {{ item.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import API from '@api/index.js';
import { ref, onMounted } from 'vue';
import { getCartList } from '@shared/services';
import { cloneDeep } from 'lodash-es';
import { toast } from '@shared/utils/biz_utils';
import { Bridge } from "@shared/bridge";

// 已保存的默认地址（用于顶部展示）
const AddressInfo = ref(null);
// 用户临时选择的地址（弹窗内操作）
const chooseAddress = ref(null);
// 当前激活的地址层级
const currentAddressType = ref('province');
// 当前显示的列表数据
const showList = ref([]);
// 临时禁用 hover
const disableHover = ref(false);
// 各层级数据缓存
const provinceList = ref([]);
const cityList = ref([]);
const districtList = ref([]);
const subdistrictList = ref([]);
// 接口请求中标志，防止重复点击
const isAPI_Inprogress = ref(false);

onMounted(async () => {
  try {
    // 获取默认地址
    const res = await API.getDefaultAddressData({ optType: 1 });
    if (res?.code === '00000') {
      const data = res.t || {};
      const { provinceNo, provinceCode, cityNo, cityCode, countyNo, countyCode, townshipNo = '', townshipCode = '' } = data;
      const paramsInfo = {
        provinceCode: provinceNo,
        provinceName: provinceCode,
        cityCode: cityNo,
        cityName: cityCode,
        districtCode: countyNo,
        districtName: countyCode,
        subdistrictCode: townshipNo,
        subdistrictName: townshipCode,
      };
      AddressInfo.value = cloneDeep(paramsInfo);
      chooseAddress.value = cloneDeep(paramsInfo);

      // 加载所有层级数据（根据默认地址逐级加载）
      const provinceRes = await API.getProvinceAddressData();
      provinceList.value = provinceRes?.t || [];
      showList.value = provinceRes?.t || [];

      const getCityParams = { provincename: AddressInfo.value.provinceName };
      const cityRes = await API.getCityAddressData(getCityParams);
      cityList.value = cityRes?.t || [];

      const getDistrictParams = { provincename: AddressInfo.value.provinceName, cityname: AddressInfo.value.cityName };
      const districtRes = await API.getAreaAddressData(getDistrictParams);
      districtList.value = districtRes?.t || [];

      const getSubdistrictParams = {
        provincename: AddressInfo.value.provinceName,
        cityname: AddressInfo.value.cityName,
        countryname: AddressInfo.value.districtName,
      };
      const subdistrictRes = await API.getSubdistrictAddressData(getSubdistrictParams);
      subdistrictList.value = subdistrictRes?.t || [];
    } else {
      // 无默认地址时只加载省份列表
      const provinceRes = await API.getProvinceAddressData();
      provinceList.value = provinceRes?.t || [];
      showList.value = provinceRes?.t || [];
    }
  } catch (err) {
    console.error('获取默认收货地址出错', err);
  }
});

// 判断当前项是否选中（高亮）
const isItemSelected = (code) => {
  if (!chooseAddress.value) return false;
  switch (currentAddressType.value) {
    case 'province':
      return chooseAddress.value.provinceCode === code;
    case 'city':
      return chooseAddress.value.cityCode === code;
    case 'district':
      return chooseAddress.value.districtCode === code;
    case 'subdistrict':
      return chooseAddress.value.subdistrictCode === code;
    default:
      return false;
  }
};

// 点击层级标签
const clickTitle = (typeKey) => {
  currentAddressType.value = typeKey;
  // 根据类型切换列表数据
  switch (typeKey) {
    case 'province':
      showList.value = provinceList.value;
      break;
    case 'city':
      showList.value = cityList.value;
      break;
    case 'district':
      showList.value = districtList.value;
      break;
    case 'subdistrict':
      showList.value = subdistrictList.value;
      break;
    default:
      showList.value = [];
  }
};

// 点击列表项选择地址
const clickArea = (typeKey, item) => {
  if (isAPI_Inprogress.value) return;
  isAPI_Inprogress.value = true;

  switch (typeKey) {
    case 'province': {
      // 清空所有下级，只保留省份
      const tempInfo = chooseAddress.value;
      for (const key in tempInfo) {
        if (tempInfo.hasOwnProperty(key)) {
          tempInfo[key] = '';
        }
      }
      chooseAddress.value.provinceName = item.name;
      chooseAddress.value.provinceCode = item.code;

      const getCityParams = { provincename: chooseAddress.value.provinceName };
      API.getCityAddressData(getCityParams)
      .then((cityRes) => {
        showList.value = cityRes.t || [];
        cityList.value = cityRes.t || [];
        currentAddressType.value = 'city';
      })
      .finally(() => {
        isAPI_Inprogress.value = false;
      });
      break;
    }

    case 'city': {
      chooseAddress.value.cityName = item.name;
      chooseAddress.value.cityCode = item.code;
      // 清空下级
      chooseAddress.value.districtName = null;
      chooseAddress.value.districtCode = null;
      chooseAddress.value.subdistrictName = null;
      chooseAddress.value.subdistrictCode = null;

      const getDistrictParams = {
        provincename: chooseAddress.value.provinceName,
        cityname: chooseAddress.value.cityName,
      };
      API.getAreaAddressData(getDistrictParams)
      .then((districtRes) => {
        showList.value = districtRes.t || [];
        districtList.value = districtRes.t || [];
        currentAddressType.value = 'district';
      })
      .finally(() => {
        isAPI_Inprogress.value = false;
      });
      break;
    }

    case 'district': {
      chooseAddress.value.districtName = item.name;
      chooseAddress.value.districtCode = item.code;
      // 清空下级
      chooseAddress.value.subdistrictName = null;
      chooseAddress.value.subdistrictCode = null;

      const getSubdistrictParams = {
        provincename: chooseAddress.value.provinceName,
        cityname: chooseAddress.value.cityName,
        countryname: chooseAddress.value.districtName,
      };
      API.getSubdistrictAddressData(getSubdistrictParams)
      .then((subdistrictRes) => {
        showList.value = subdistrictRes.t || [];
        subdistrictList.value = subdistrictRes.t || [];
        currentAddressType.value = 'subdistrict';
      })
      .finally(() => {
        isAPI_Inprogress.value = false;
      });
      break;
    }

    case 'subdistrict': {
      chooseAddress.value.subdistrictName = item.name;
      chooseAddress.value.subdistrictCode = item.code;
      // 保存地址前切换回省份列表（为下次打开弹窗做准备）
      showList.value = provinceList.value;
      // 取消hover效果 让地址选择部分消失
      disableHover.value = true;
      // 恢复hover效果
      setTimeout(() => {
        disableHover.value = false;
      }, 200);
      const {
        subdistrictCode,
        subdistrictName,
        districtCode,
        districtName,
        cityCode,
        cityName,
        provinceCode,
        provinceName,
      } = chooseAddress.value;

      const saveAddressParams = {
        optType: 0,
        townshipCode: subdistrictCode,
        townshipName: subdistrictName,
        areaCode: districtCode,
        areaName: districtName,
        cityCode: cityCode,
        cityName: cityName,
        provinceCode: provinceCode,
        provinceName: provinceName,
      };

      API.getDefaultAddressData(saveAddressParams)
      .then((saveRes) => {
        if (saveRes?.code === '00000') {
          AddressInfo.value = cloneDeep(chooseAddress.value);
          handleReRenderCart();
          currentAddressType.value = 'province';
        } else {
          toast(saveRes?.msg || '地址保存失败');
        }
      })
      .finally(() => {
        isAPI_Inprogress.value = false;
      });
      break;
    }

    default:
      isAPI_Inprogress.value = false;
  }
};

// 刷新购物车
const handleReRenderCart = () => {
  getCartList()
    .then((data) => {
      if (!data?.success) {
        toast(data?.resultMsg || '刷新购物车失败');
        return;
      }
      Bridge.emit(Bridge.EventType.renderCart, data);
    })
    .catch(() => {
      toast('购物车刷新失败');
    });
};
</script>

<style lang="less" scoped>
.address-box {
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
  &-left {
    width: 58px;
    line-height: 30px;
    font-weight: 600;
    font-size: 13px;
    color: #454545;
  }
  &-right {
    min-width: 0;
    padding: 0 5px;
    background: #fff;
    border: 1px solid #dadada;
    height: 28px;
    font-size: 12px;
    color: #434242;
    line-height: 28px;
    display: flex;
    span {
      margin: 0 0 0 5px;
    }
  }
  &-areabox {
    right: 0px;
    top: 28px;
    box-shadow: 0 0 4px 0 #e0e0e0;
    position: absolute;
    z-index: 10;
    background: #ffffff;
    border: 1px solid #dadada;
    color: #434242;
    font-size: 12px;
    display: none;
    flex-direction: column;
    width: 540px;
    min-height: 350px;
    &-title {
      display: flex;
      align-items: center;
      &-item {
        padding: 8px 14px;
        background: #f6f6f6;
        border: 1px solid #e0e0e0;
        border-top: none;
        cursor: pointer;
      }
    }
    &-content {
      display: flex;
      flex-wrap: wrap;
      overflow-y: auto;
    }
    &-item {
      display: flex;
      font-size: 12px;
      width: auto;
      padding: 0;
      cursor: pointer;
      margin: 10px 0 5px 20px;
      width: 115px;
      &:hover {
        color: #ff2f2f !important;
      }
    }
  }
}
.icon-address-arrow {
  float: right;
  margin: 6px 3px 0 12px;
  width: 15px;
  height: 15px;
  background-image: url(//p4.lefile.cn/product/adminweb/2021/04/03/cdcp8BkWfE007WMvFWooZzuVa-1625.png);
  background-position: -47px 0px;
  background-repeat: no-repeat;
}
.itemActive {
  color: #ff2f2f;
}
.tabActive {
  background: #ffffff !important;
  border: none !important;
}

.address-box:hover .address-box-areabox {
  display: flex;
}

.address-box:hover .icon-address-arrow {
  transform: rotate(180deg);
}
// 点击第四级区域（街道、镇）的时候，需要让hover消失
.address-box.disable-hover:hover .address-box-areabox {
  display: none !important;
}
// 点击第四级区域（街道、镇）的时候，需要箭头图标样式还原
.address-box.disable-hover:hover .icon-address-arrow {
  transform: rotate(0deg) !important;
}
</style>